import React from "react";
import { PhoneIcon, MailIcon, QuoteIcon } from "lucide-react";
import { ConsultationForm } from "../shared/ConsultationForm";
import { RooflineMark } from "../shared/RooflineMark";

// [Conversion] [Lead Gen] Progressive consultation request with reassurance, a real client quote and phone/email.
const nextSteps = [{
  title: "We review your details",
  text: "The owners look at what you’ve told us about your project."
}, {
  title: "We get in touch",
  text: "By your preferred method, to talk through your plans."
}, {
  title: "We meet",
  text: "We confirm a suitable time for your consultation."
}];
type ConsultationSectionProps = {
  headingLevel?: "h1" | "h2";
};
export function ConsultationSection({
  headingLevel = "h2"
}: ConsultationSectionProps) {
  const Heading = headingLevel;
  return <section id="consultation" aria-labelledby="consult-heading" className="bg-paper-deep py-20 md:py-28" data-id="element-165" data-mp="3c6yy5tgnfs91">
      <div className="mx-auto max-w-site px-5 md:px-10" data-id="element-166" data-mp="3h5a59wfk7onk">
        <div className="mx-auto max-w-3xl text-center" data-id="element-167" data-mp="1y0v5dlfofx05">
          <RooflineMark className="mx-auto text-ocean" data-id="element-168" data-mp="1xza9nshmm80p" />
          <Heading id="consult-heading" className="mt-5 text-[34px] font-semibold leading-[1.06] tracking-[-0.02em] md:text-[52px]" data-id="element-169" data-mp="1tzjktubnu1yt">
            Let’s talk about your next home.
          </Heading>
          <p className="mx-auto mt-5 max-w-2xl text-[17px] leading-relaxed text-slate md:text-[19px]" data-id="element-170" data-mp="2307s866jt42q">
            Tell us a little about your plans. We’ll help you work out the next step, whether you’re comparing designs, rebuilding,
            building on a rear block or planning an investment.
          </p>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-12 lg:gap-10" data-id="element-171" data-mp="1p18c5mx3pxi6">
          <aside className="space-y-6 lg:col-span-5" data-id="element-172" data-mp="1k9qbz12wk0y9">
            <div className="rounded-[28px] bg-white p-7 md:p-8" data-id="element-173" data-mp="1k85h1acoefzv">
              <h3 className="text-[20px] font-semibold" data-id="element-174" data-mp="wj47tkw0r0tj">What happens next</h3>
              <ol className="mt-5 space-y-5" data-id="element-175" data-mp="rl2p512i93il">
                {nextSteps.map((step, index) => <li key={step.title} className="flex gap-4" data-id="element-176" data-mp="2rnam0t0iq471" data-mpi={index}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-charcoal font-heading text-[15px] font-semibold text-paper" data-id="element-177" data-mp="2rky4ui2l3xv1">{index + 1}</span>
                    <span data-id="element-178" data-mp="31ddkh8qesacr">
                      <span className="block font-semibold" data-id="element-179" data-mp="31f7x7zrj0w5r">{step.title}</span>
                      <span className="mt-0.5 block text-[15px] leading-relaxed text-slate" data-id="element-180" data-mp="35dnowgyjyspi">{step.text}</span>
                    </span>
                  </li>)}
              </ol>
              <p className="mt-5 border-t border-line pt-4 text-[14px] text-slate" data-id="element-181" data-mp="2dnsy0fv1quyq">A request isn’t a confirmed appointment. We’ll confirm a time with you.</p>
            </div>

            <figure className="rounded-[28px] bg-charcoal p-7 text-paper md:p-8" data-id="element-182" data-mp="2mp0km0o7a7cz">
              <QuoteIcon className="h-7 w-7 text-ocean" aria-hidden="true" data-id="element-183" data-mp="3t4t5iycbhp0v" />
              <blockquote className="mt-4 text-[17px] leading-relaxed" data-id="element-184" data-mp="1xqhgfrrzyuv">
                “They were always happy to take our calls (and we made many), meet us on site, and accommodated any questions we had.”
              </blockquote>
              <figcaption className="mt-4 text-[14px] text-paper/70" data-id="element-185" data-mp="73lr8t3bo22b">Jemille &amp; Anthony</figcaption>
            </figure>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2" data-id="element-186" data-mp="2aar1at0mjjd">
              <a href="tel:+61862850876" className="group flex items-center gap-3 rounded-full border border-line bg-white px-5 py-3.5 transition-colors duration-150 ease-out hover:border-charcoal" data-id="element-187" data-mp="3uev81uotu5o7">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ocean text-charcoal" data-id="element-188" data-mp="3kgp8hjqhtbmi"><PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-189" data-mp="3fj5g7fwymybk" /></span>
                <span className="text-[15px] font-semibold" data-id="element-190" data-mp="3bhakcqxstd56">08 6285 0876</span>
              </a>
              <a href="mailto:admin@oceansideliving.com.au" className="group flex min-w-0 items-center gap-3 rounded-full border border-line bg-white px-5 py-3.5 transition-colors duration-150 ease-out hover:border-charcoal" data-id="element-191" data-mp="1driwlzfee5p4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ocean text-charcoal" data-id="element-192" data-mp="1gtx7q0h8wr3u"><MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-193" data-mp="1lqdxez43fdj1" /></span>
                <span className="truncate text-[15px] font-semibold" data-id="element-194" data-mp="17spa7kmjzuat">Email the team</span>
              </a>
            </div>
          </aside>
          <div className="lg:col-span-7" data-id="element-195" data-mp="1brpqavdnciu4">
            <ConsultationForm data-id="element-196" data-mp="y2e2dmzr10yn" />
          </div>
        </div>
      </div>
    </section>;
}